import React, { useState, useEffect } from "react";
import dayjs from "dayjs";
import Markdown from "markdown-to-jsx";
import SyntaxHighlighter from "react-syntax-highlighter";
import { docco } from "react-syntax-highlighter/dist/esm/styles/hljs";
import "./BlogPost.css";
import { config } from "../../config";
import GithubReactionTextCard from "../../Components/GithubReactionTextCard/GithubReactionTextCard";

function readingTime(text) {
  const words = text.trim().split(/\s+/).length;
  return { minutes: Math.max(1, Math.round(words / 200)) };
}

const REACTION_MAP = {
  "+1": "THUMBS_UP",
  "-1": "THUMBS_DOWN",
  laugh: "LAUGH",
  hooray: "HOORAY",
  confused: "CONFUSED",
  heart: "HEART",
  rocket: "ROCKET",
  eyes: "EYES"
};

const EMOJI_MAP = {
  THUMBS_UP: "\u{1F44D}",
  THUMBS_DOWN: "\u{1F44E}",
  LAUGH: "\u{1F604}",
  HOORAY: "\u{1F389}",
  CONFUSED: "\u{1F615}",
  HEART: "\u{2764}\u{FE0F}",
  ROCKET: "\u{1F680}",
  EYES: "\u{1F440}"
};

export default function BlogHome() {
  const [blog, setBlogs] = useState(null);
  const [reactionCounter, setReactionCounter] = useState([]);
  const [showReactionLink, setShowReactionLink] = useState(false);
  const issueNumber = parseInt(window.location.href.split("/").pop());

  useEffect(() => {
    getBlogsFromGithubIssues();
  }, []);

  function getBlogsFromGithubIssues() {
    const issueUrl = `https://api.github.com/repos/${config.githubUserName}/${config.githubRepo}/issues/${issueNumber}`;

    Promise.all([
      fetch(issueUrl, {
        headers: { Accept: "application/vnd.github.full+json" }
      }).then(r => r.json()),
      fetch(`${issueUrl}/reactions`, {
        headers: { Accept: "application/vnd.github.squirrel-girl-preview+json" }
      }).then(r => r.json())
    ])
      .then(([issue, reactions]) => {
        const blogData = {
          title: issue.title,
          body: issue.body,
          bodyHTML: issue.body_html,
          bodyText: issue.body_text,
          number: issue.number,
          url: issue.html_url,
          author: {
            url: issue.user.html_url,
            avatarUrl: issue.user.avatar_url,
            login: issue.user.login
          },
          updatedAt: issue.updated_at,
          id: issue.id.toString()
        };

        setBlogs(blogData);
        document.title = `${blogData.title} — ${config.title}`;

        const reactionNodes = (Array.isArray(reactions) ? reactions : []).map(r => ({
          content: REACTION_MAP[r.content] || r.content.toUpperCase(),
          user: { id: r.user.id.toString(), login: r.user.login }
        }));

        const reactionArray = reactionNodes.map(element => ({
          by: element.user.login,
          emoji: EMOJI_MAP[element.content] || ""
        }));
        setReactionCounter(reactionArray);
      })
      .catch(err => console.error(err));
  }

  const HyperLink = ({ children, ...props }) => (
    <a href={props.href} target="_blank" rel="noopener noreferrer" className="blog-post-anchor">
      {children}
      <style jsx="true">
        {`
          a {
            color: #484848;
            font-weight: 400;
          }
        `}
      </style>
    </a>
  );

  const CodeBlock = ({ children }) => (
    <SyntaxHighlighter language="typescript" style={docco}>
      {children.props.children}
    </SyntaxHighlighter>
  );

  return (
    <div>
      {blog && blog.title && (
        <div className="blog-view">
          <h1 className="blog-title">{blog.title}</h1>
          <div>
            <div className="author-details">
              <img className="avatar" src={blog.author.avatarUrl} alt={blog.author.login} />
              <div>
                <p className="author-name">{blog.author.login}</p>
                <p className="blog-date">
                  {dayjs(blog.updatedAt).format("DD MMM YYYY")} . {readingTime(blog.body).minutes} Min Read . <a href={blog.url} target="_blank" rel="noopener noreferrer">View On Github</a>
                </p>
              </div>
            </div>
          </div>
          <Markdown
            options={{
              overrides: {
                a: {
                  component: HyperLink
                },
                pre: {
                  component: CodeBlock
                }
              }
            }}
          >
            {blog.body}
          </Markdown>
          {showReactionLink && (
            <span className="reaction-github-emoji anim-scale-in">
              <GithubReactionTextCard link={blog.url} />
            </span>
          )}
          {reactionCounter.length > 0 && (
            <div className="reactions-display" onClick={() => setShowReactionLink(!showReactionLink)}>
              {reactionCounter.map((r, i) => (
                <span key={i} className="reaction-emoji" title={r.by}>{r.emoji}</span>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
